import InitHomeClientLogic from "./HomeClient.logic";
import { useState } from "react";
import { t } from "i18next";
import { formatDateTime, formatBookingLongDate, formatBookingTime } from "../../helpers/dateFormat";
import SurveyComponent from "../Survey/Survey.component";

const HomeClientComponent: React.FC = () => {
    const { client, appSettings, onSettings, onTrainings, onCancel } = InitHomeClientLogic();
    const [activeTab, setActiveTab] = useState("Početna");
    const [showModal, setShowModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

    // Determine payment status based on Grupa counter only (klijenti koji se zakazuju su za grupne)
    const grupaCounter = client?.counters?.find((c: any) => c.trainingCategoryId === 1)?.counter ?? 0;
    const isPaid = grupaCounter >= 0;

    const handleCancelClick = (id: number) => {
        setSelectedBookingId(id);
        setShowModal(true);
    };

    const confirmCancel = () => {
        if (selectedBookingId !== null) {
            onCancel(selectedBookingId);
        }
        setShowModal(false);
    };

    const closeModal = () => setShowModal(false);

    return (
        <div style={{ paddingBottom: '60px' }}> {/* Padding to avoid content overlap with fixed tabs */}
            {/* Modal for Confirmation */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '5px',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <p>{`${t("Da li ste sigurni da želite da otkažete ovaj trening?")}`}</p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginTop: '20px'
                        }}>
                            <button onClick={closeModal} className="btn-secondary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>Ne</button>
                            <button onClick={confirmCancel} className="btn-primary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>Da</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom baner na vrhu (admin) - kad je aktivan, upitnik se ne prikazuje */}
            {appSettings?.bannerActive && appSettings?.bannerMessage?.trim() && (
                <div style={{
                    maxWidth: 480,
                    margin: "0 auto 16px",
                    background: appSettings.bannerBgColor || "#d4f0d4",
                    color: appSettings.bannerTextColor || "#2d862d",
                    borderRadius: 10,
                    padding: "12px 14px",
                    fontWeight: 600,
                    textAlign: "center",
                    whiteSpace: "pre-wrap",
                }}>
                    {appSettings.bannerMessage}
                </div>
            )}

            {/* Upitnik: samo ako baner nije aktivan i upitnik je ukljucen u podesavanjima */}
            {!appSettings?.bannerActive && appSettings?.surveyEnabled !== false && (
                <SurveyComponent />
            )}

            <div style={{ maxWidth: 480, margin: "0 auto", padding: "20px 16px" }}>
                {/* Paket kartica */}
                <h1 style={{ textAlign: "center", marginBottom: 12 }}>{`${t("Paket")}`}</h1>
                <div className="card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 600 }}>{`${t("Grupa")}`}</span>
                        <span style={{
                            backgroundColor: isPaid ? "var(--success-bg)" : "var(--danger-bg)",
                            color: isPaid ? "var(--success-text)" : "var(--danger-text)",
                            padding: "5px 12px",
                            borderRadius: 20,
                            fontSize: "0.85rem",
                            fontWeight: 600,
                        }}>
                            {isPaid ? `${t("Plaćeno")}` : `${t("Nije plaćeno")}`}
                        </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                        <span style={{ opacity: 0.8 }}>{`${t("Preostali broj treninga")}`}</span>
                        <span style={{ fontWeight: 700, fontSize: "1.1rem" }}>
                            {client?.counters?.find((c: any) => c.trainingCategoryId === 1)?.counter ?? 0}
                        </span>
                    </div>
                    {client?.packageExpiration && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: 12, paddingTop: 12, borderTop: "1px solid rgba(59,88,85,0.08)" }}>
                            <span style={{ opacity: 0.8 }}>{`${t("Paket ističe")}`}:</span>
                            <span style={{ fontWeight: 500, textAlign: "right" }}>
                                {formatDateTime(client.packageExpiration)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Zakazani treninzi */}
                <h1 style={{ textAlign: "center", marginTop: 28, marginBottom: 12 }}>{`${t("Zakazani treninzi")}`}</h1>
                {client?.bookings?.length ? (
                    client.bookings.map((booking: any, index: number) => (
                        <div key={index} className="card">
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 600 }}>{formatBookingLongDate(booking.date)}</div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                                        <span style={{
                                            backgroundColor: "var(--success-bg)",
                                            color: "var(--success-text)",
                                            padding: "3px 10px",
                                            borderRadius: 20,
                                            fontSize: "0.85rem",
                                            fontWeight: 600,
                                        }}>
                                            {formatBookingTime(booking.date)}
                                        </span>
                                        <span style={{ opacity: 0.75, fontSize: "0.9rem" }}>{`${t(booking.location)}`}</span>
                                    </div>
                                    {booking.user && (
                                        <div style={{ fontSize: "0.82rem", opacity: 0.7, marginTop: 6 }}>
                                            {`${t("Trener")}`}: {booking.user.firstName} {booking.user.lastName}
                                        </div>
                                    )}
                                </div>
                                <button type="button" className="btn-danger" style={{
                                    padding: "8px 16px",
                                    width: "auto",
                                    fontSize: "0.85rem",
                                    borderRadius: 8,
                                    flexShrink: 0,
                                }} onClick={() => { handleCancelClick(booking.id) }}>
                                    {`${t("Otkaži")}`}
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ textAlign: "center", color: "var(--muted)", marginTop: 24 }}>
                        <span style={{ fontSize: 32 }}>📅</span>
                        <p>{`${t("Nemate zakazanih treninga")}`}</p>
                    </div>
                )}
            </div>

            {/* Bottom Navigation Tabs */}
            <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                backgroundColor: 'var(--white)',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-around',
                padding: '10px 8px',
                gap: '8px',
                zIndex: 100
            }}>
                <button
                    style={{
                        flex: 1,
                        maxWidth: 160,
                        padding: '10px 12px',
                        background: activeTab === "Početna" ? 'var(--secondary)' : 'transparent',
                        color: 'var(--text)',
                        fontWeight: activeTab === "Početna" ? 600 : 400,
                        borderRadius: 10,
                    }}
                    onClick={() => setActiveTab("Početna")}
                >
                    {`${t("Početna")}`}
                </button>
                <button
                    style={{
                        flex: 1,
                        maxWidth: 160,
                        padding: '10px 12px',
                        background: activeTab === "Kalendar" ? 'var(--secondary)' : 'transparent',
                        color: 'var(--text)',
                        fontWeight: activeTab === "Kalendar" ? 600 : 400,
                        borderRadius: 10,
                    }}
                    onClick={() => {
                        setActiveTab("Kalendar");
                        onTrainings();
                    }}
                >
                    {`${t("Kalendar")}`}
                </button>
                <button
                    style={{
                        flex: 1,
                        maxWidth: 160,
                        padding: '10px 12px',
                        background: activeTab === "Podešavanja" ? 'var(--secondary)' : 'transparent',
                        color: 'var(--text)',
                        fontWeight: activeTab === "Podešavanja" ? 600 : 400,
                        borderRadius: 10,
                    }}
                    onClick={() => {
                        setActiveTab("Podešavanja");
                        onSettings();
                    }}
                >
                    {`${t("Podešavanja")}`}
                </button>
            </div>
        </div>
    );
}

export default HomeClientComponent;
